import { useMemo, useState } from "react";
import { Slider } from "@/components/ui/slider";
import { formatINR, formatINRFull } from "@/lib/api";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { Button } from "@/components/ui/button";

export const EMICalculator = ({ onQuote }) => {
  const [amount, setAmount] = useState(500000);
  const [rate, setRate] = useState(11);
  const [tenure, setTenure] = useState(36);

  const { emi, totalInterest, totalPayable } = useMemo(() => {
    const P = amount;
    const r = rate / 12 / 100;
    const n = tenure;
    const emiVal = (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    const total = emiVal * n;
    return {
      emi: isFinite(emiVal) ? emiVal : 0,
      totalInterest: total - P,
      totalPayable: total,
    };
  }, [amount, rate, tenure]);

  const data = [
    { name: "Principal", value: amount },
    { name: "Interest", value: Math.max(totalInterest, 0) },
  ];
  const COLORS = ["#0F172A", "#10B981"];

  return (
    <section id="emi" className="py-20 sm:py-24 bg-slate-50 grain-bg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-3">EMI Calculator</div>
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-medium text-slate-900">
            Know exactly what you'll pay. Before you apply.
          </h2>
          <p className="mt-3 text-slate-600">
            Adjust the sliders. Real-time EMI with the interest-vs-principal breakdown — no formulas, no surprises.
          </p>
        </div>

        <div className="mt-12 grid lg:grid-cols-12 gap-8">
          {/* Sliders */}
          <div className="lg:col-span-7 space-y-8 bg-white border border-slate-200 rounded-2xl p-6 lg:p-10">
            <SliderRow
              testId="emi-amount"
              label="Loan Amount"
              value={amount}
              display={formatINRFull(amount)}
              onChange={(v) => setAmount(v[0])}
              min={50000}
              max={5000000}
              step={10000}
              leftHint="₹50,000"
              rightHint="₹50,00,000"
            />
            <SliderRow
              testId="emi-rate"
              label="Interest Rate (p.a.)"
              value={rate}
              display={`${rate.toFixed(2)}%`}
              onChange={(v) => setRate(v[0])}
              min={7}
              max={30}
              step={0.05}
              leftHint="7%"
              rightHint="30%"
            />
            <SliderRow
              testId="emi-tenure"
              label="Loan Tenure"
              value={tenure}
              display={`${tenure} months (${(tenure / 12).toFixed(1)} yrs)`}
              onChange={(v) => setTenure(v[0])}
              min={6}
              max={96}
              step={1}
              leftHint="6 mo"
              rightHint="96 mo"
            />

            <div className="pt-6 border-t border-slate-200 grid grid-cols-3 gap-6">
              <MetricCard label="Monthly EMI" value={formatINRFull(emi)} testId="emi-monthly" accent />
              <MetricCard label="Total Interest" value={formatINR(totalInterest)} testId="emi-interest" />
              <MetricCard label="Total Payable" value={formatINR(totalPayable)} testId="emi-total" />
            </div>

            <Button
              data-testid="emi-get-quote"
              onClick={onQuote}
              className="w-full h-12 bg-emerald-500 hover:bg-emerald-600 text-white rounded-md font-semibold active:scale-95 transition-all"
            >
              Lock these numbers — Get personalized quote
            </Button>
          </div>

          {/* Chart */}
          <div className="lg:col-span-5 bg-slate-900 text-white rounded-2xl p-8 flex flex-col">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400 mb-2">Breakdown</div>
            <div className="font-display text-2xl font-medium mb-6">Where your money goes</div>

            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={data} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={2} dataKey="value">
                    {data.map((_, i) => <Cell key={i} fill={COLORS[i]} stroke="none" />)}
                  </Pie>
                  <Tooltip
                    formatter={(v) => formatINRFull(v)}
                    contentStyle={{ backgroundColor: "#0F172A", border: "1px solid #334155", borderRadius: 8 }}
                    labelStyle={{ color: "#fff" }}
                    itemStyle={{ color: "#fff" }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="mt-6 space-y-3">
              <LegendRow color="#0F172A" border label="Principal" value={formatINRFull(amount)} pct={((amount / (amount + totalInterest)) * 100).toFixed(1)} />
              <LegendRow color="#10B981" label="Interest" value={formatINRFull(totalInterest)} pct={((totalInterest / (amount + totalInterest)) * 100).toFixed(1)} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const SliderRow = ({ testId, label, value, display, onChange, min, max, step, leftHint, rightHint }) => (
  <div>
    <div className="flex justify-between items-baseline mb-3">
      <label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{label}</label>
      <span className="font-display text-lg font-semibold text-slate-900" data-testid={`${testId}-value`}>{display}</span>
    </div>
    <Slider data-testid={testId} min={min} max={max} step={step} value={[value]} onValueChange={onChange} />
    <div className="flex justify-between text-[10px] text-slate-400 mt-1.5 font-semibold">
      <span>{leftHint}</span><span>{rightHint}</span>
    </div>
  </div>
);

const MetricCard = ({ label, value, testId, accent }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">{label}</div>
    <div className={`font-display font-semibold text-xl sm:text-2xl ${accent ? "text-emerald-600" : "text-slate-900"}`} data-testid={testId}>{value}</div>
  </div>
);

const LegendRow = ({ color, label, value, pct, border }) => (
  <div className="flex items-center justify-between text-sm">
    <div className="flex items-center gap-2">
      <span className={`w-3 h-3 rounded-sm ${border ? "border border-white/40" : ""}`} style={{ backgroundColor: color }} />
      <span className="text-slate-300">{label}</span>
    </div>
    <div className="flex items-center gap-3">
      <span className="text-white font-semibold">{value}</span>
      <span className="text-xs text-slate-400 w-12 text-right">{pct}%</span>
    </div>
  </div>
);
